import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Car,
  ClipboardList,
  ExternalLink,
  FileText,
  Info,
  Mail,
  PencilLine,
  RotateCw,
  RefreshCcw,
  ShoppingCart,
  Users,
} from "lucide-react";
import { appConfig } from "@ir/config";
import {
  deriveMarketplacePricingSummary,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPricingOgiIndicator,
  formatPricingOgiTooltip,
  formatYesNo,
  fullName,
  getQuoteById,
  retryNoetica,
  sendCustomerQuoteEmail,
} from "@ir/services";
import {
  Badge,
  Button,
  Card,
  CopyableId,
  DetailList,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusPill,
  TabPanel,
  Tabs,
  useToast,
} from "@ir/ui";
import { useAsyncData } from "../hooks/useAsyncData";
import { useAuth } from "../auth/AuthProvider";
import { DriverPanel, NoAdditionalDrivers } from "../features/quote-details/DriverPanel";
import { ProviderQuotesPanel } from "../features/quote-details/ProviderQuotesPanel";
import { VehiclePanel } from "../features/quote-details/VehiclePanel";

type TabId = "summary" | "driver" | "additional" | "vehicle" | "quotes";

export function QuoteDetailsPage() {
  const { id = "" } = useParams();
  const [tab, setTab] = useState<TabId>("summary");
  const [retryingNoetica, setRetryingNoetica] = useState<"initial" | "buy" | null>(null);
  const [sendingCustomerEmail, setSendingCustomerEmail] = useState(false);
  const [customerEmailQueued, setCustomerEmailQueued] = useState(false);
  const { can } = useAuth();
  const toast = useToast();

  const result = useAsyncData((signal) => getQuoteById(id, signal), [id]);
  const quote = result.data;

  useEffect(() => {
    setCustomerEmailQueued(false);
  }, [id]);

  if (result.loading) {
    return <LoadingState message="Loading quote…" />;
  }

  if (result.error || !quote) {
    return (
      <>
        <PageHeader
          icon={ClipboardList}
          title="Quote"
          breadcrumbs={[{ label: "Quotes", href: "/quotes" }, { label: "Not found" }]}
          linkComponent={({ href, children }) => <Link to={href}>{children}</Link>}
        />
        <Card padded={false}>
          <ErrorState
            title="We could not open this quote"
            message={result.error ?? "That quote no longer exists."}
            onRetry={result.reload}
          />
        </Card>
      </>
    );
  }

  const mainDriver = quote.driver?.[0];
  const additionalDrivers = quote.driver?.slice(1) ?? [];
  const vehicles = quote.vehicle ?? [];
  const baseQuotes = quote.base_quotes ?? [];
  const pricingSummary = deriveMarketplacePricingSummary(baseQuotes);
  const displayStatus =
    pricingSummary.quotedProviderCount > 0 || pricingSummary.allExpectedProvidersResolved
      ? pricingSummary.overallStatus
      : (quote.state ?? "Pending");
  const buyIntent = quote.buy_intent;
  const noeticaDelivery = quote.noetica_delivery;
  const customerEmailInProgress =
    customerEmailQueued ||
    Boolean(quote.customer_quote_email_sent_at) ||
    Boolean(quote.customer_quote_notification_claimed_at);
  const buyIntentTone =
    buyIntent?.status.toLowerCase() === "queued"
      ? "success"
      : buyIntent?.status.toLowerCase() === "failed"
        ? "danger"
        : "warning";

  const customerUrl = `${appConfig.publicQuoteScreenUrl}/?driveremail=${encodeURIComponent(
    mainDriver?.email ?? "",
  )}&quoteid=${encodeURIComponent(quote.id)}`;

  const handleNoeticaRetry = async (kind: "initial" | "buy") => {
    setRetryingNoetica(kind);
    try {
      const updated = await retryNoetica(quote.id, kind);
      const status = kind === "buy" ? updated.buy_intent?.status : updated.noetica_delivery?.status;
      toast.success(
        "Noetica retry completed",
        status === "Queued"
          ? "The customer was accepted into the Noetica queue."
          : `Current delivery status: ${status ?? "unknown"}.`,
      );
      result.reload();
    } catch (caught) {
      toast.error(
        "Noetica retry failed",
        caught instanceof Error ? caught.message : "Please try again.",
      );
    } finally {
      setRetryingNoetica(null);
    }
  };

  const handleSendCustomerEmail = async () => {
    setSendingCustomerEmail(true);
    try {
      await sendCustomerQuoteEmail(quote.id);
      setCustomerEmailQueued(true);
      toast.success(
        "Customer email queued",
        "The existing quote email worker will send it shortly.",
      );
      result.reload();
    } catch (caught) {
      toast.error(
        "Could not queue customer email",
        caught instanceof Error ? caught.message : "Please try again.",
      );
    } finally {
      setSendingCustomerEmail(false);
    }
  };

  return (
    <>
      <PageHeader
        icon={ClipboardList}
        title={fullName(
          [mainDriver?.title, mainDriver?.first_name, mainDriver?.last_name],
          "Quote details",
        )}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusPill status={displayStatus} />
            {buyIntent && (
              <Badge icon={ShoppingCart} tone={buyIntentTone}>
                Buy intent: {buyIntent.status}
              </Badge>
            )}
            <span className="text-xs text-content-subtle">
              PRICING (OGI){" "}
              <span
                className="cursor-help underline decoration-dotted underline-offset-4"
                title={formatPricingOgiTooltip(pricingSummary)}
              >
                {formatPricingOgiIndicator(pricingSummary)}
              </span>
            </span>
            <span className="text-xs">Created {formatDateTime(quote.created_at)}</span>
          </span>
        }
        breadcrumbs={[{ label: "Quotes", href: "/quotes" }, { label: "Details" }]}
        linkComponent={({ href, children }) => <Link to={href}>{children}</Link>}
        actions={
          <>
            <Link to="/quotes">
              <Button variant="ghost" icon={ArrowLeft}>
                Back
              </Button>
            </Link>
            <Button
              variant="secondary"
              icon={RotateCw}
              loading={result.refreshing}
              onClick={result.reload}
            >
              Refresh
            </Button>
            <Link
              to={`/step-forms/fast-food?sourceQuoteId=${encodeURIComponent(quote.id)}`}
            >
              <Button variant="secondary" icon={PencilLine}>
                Edit and resubmit
              </Button>
            </Link>
            {can("noetica.retry") && noeticaDelivery?.status.toLowerCase() !== "queued" && (
              <Button
                variant="secondary"
                icon={RefreshCcw}
                loading={retryingNoetica === "initial"}
                onClick={() => void handleNoeticaRetry("initial")}
              >
                Retry Noetica
              </Button>
            )}
            {can("noetica.retry") &&
              buyIntent &&
              buyIntent.status.toLowerCase() !== "queued" && (
              <Button
                variant="secondary"
                icon={ShoppingCart}
                loading={retryingNoetica === "buy"}
                onClick={() => void handleNoeticaRetry("buy")}
              >
                Retry buy queue
              </Button>
            )}
            {can("quotes.email.send") &&
              !customerEmailInProgress &&
              mainDriver?.email &&
              pricingSummary.allExpectedProvidersResolved &&
              pricingSummary.quotedProviderCount > 0 && (
              <Button
                variant="secondary"
                icon={Mail}
                loading={sendingCustomerEmail}
                onClick={() => void handleSendCustomerEmail()}
              >
                Send quote email
              </Button>
            )}
            {mainDriver?.email && (
              <a href={customerUrl} target="_blank" rel="noopener noreferrer">
                <Button icon={ExternalLink}>Customer view</Button>
              </a>
            )}
          </>
        }
      />

      <Tabs
        ariaLabel="Quote sections"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "summary", label: "Summary", icon: Info },
          { id: "driver", label: "Main driver", icon: Users },
          {
            id: "additional",
            label: "Additional drivers",
            icon: Users,
            count: additionalDrivers.length,
          },
          { id: "vehicle", label: "Vehicle", icon: Car, count: vehicles.length },
          { id: "quotes", label: "Quotes", icon: FileText, count: baseQuotes.length },
        ]}
      />

      <TabPanel tabKey={tab}>
        {tab === "summary" && (
          <Card>
            <DetailList
              items={[
                { label: "Internal quote ID", value: <CopyableId value={quote.id} truncate={false} /> },
                {
                  label: "Customer reference",
                  value: quote.reference ? (
                    <CopyableId value={quote.reference} truncate={false} />
                  ) : null,
                  hideWhenEmpty: true,
                },
                { label: "Type", value: quote.type },
                {
                  label: "State",
                  value: <StatusPill status={displayStatus} />,
                },
                {
                  label: "PRICING (OGI)",
                  value: (
                    <span
                      className="cursor-help underline decoration-dotted underline-offset-4"
                      title={formatPricingOgiTooltip(pricingSummary)}
                    >
                      {formatPricingOgiIndicator(pricingSummary)}
                    </span>
                  ),
                },
                { label: "Created", value: formatDateTime(quote.created_at) },
                { label: "Quotable online", value: formatYesNo(quote.is_quotable_online) },
                {
                  label: "Insurance start",
                  value: formatDate(vehicles[0]?.cover?.date),
                },
                { label: "Source", value: quote.source, hideWhenEmpty: true },
                { label: "Leela ID", value: quote.leela_id, hideWhenEmpty: true },
                {
                  label: "Noetica delivery",
                  value: noeticaDelivery?.status ?? "Not attempted",
                },
                {
                  label: "Noetica attempts",
                  value: noeticaDelivery?.attempt_count,
                  hideWhenEmpty: true,
                },
                {
                  label: "Noetica customer ID",
                  value: quote.noetica_customer_id ? (
                    <CopyableId value={quote.noetica_customer_id} truncate={false} />
                  ) : null,
                  hideWhenEmpty: true,
                },
                {
                  label: "Noetica queue ID",
                  value: quote.dialer_queue,
                  hideWhenEmpty: true,
                },
                {
                  label: "Noetica last attempt",
                  value: formatDateTime(noeticaDelivery?.last_attempt_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Noetica next retry",
                  value: formatDateTime(noeticaDelivery?.next_retry_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Noetica error",
                  value: noeticaDelivery?.error,
                  hideWhenEmpty: true,
                },
                { label: "Sent to Granite", value: formatYesNo(quote.granite_sent) },
                { label: "Sent to OGI", value: formatYesNo(quote.ogi_sent) },
                {
                  label: "Customer email status",
                  value: quote.customer_quote_email_sent_at
                    ? "Sent"
                    : quote.customer_quote_notification_claimed_at
                      ? "Delivery pending or unconfirmed"
                      : "Not sent",
                },
                {
                  label: "Email sent at",
                  value: formatDateTime(quote.customer_quote_email_sent_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Customer SMS sent",
                  value: formatYesNo(Boolean(quote.customer_quote_sms_sent_at)),
                },
                {
                  label: "SMS sent at",
                  value: formatDateTime(quote.customer_quote_sms_sent_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Notification trigger",
                  value: quote.customer_quote_notification_trigger,
                  hideWhenEmpty: true,
                },
                {
                  label: "Notification claimed at",
                  value: formatDateTime(quote.customer_quote_notification_claimed_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Reconciliation deadline",
                  value: formatDateTime(quote.marketplace_reconciliation_deadline_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Reconciliation processed",
                  value: formatDateTime(quote.marketplace_reconciliation_processed_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy intent",
                  value: buyIntent ? (
                    <Badge icon={ShoppingCart} tone={buyIntentTone}>
                      {buyIntent.status}
                    </Badge>
                  ) : (
                    "Not selected"
                  ),
                },
                {
                  label: "Buy selected at",
                  value: formatDateTime(buyIntent?.selected_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Selected quote",
                  value: buyIntent
                    ? [buyIntent.insurer, buyIntent.cover_type, buyIntent.provider]
                        .filter(Boolean)
                        .join(" · ")
                    : null,
                  hideWhenEmpty: true,
                },
                {
                  label: "Selected annual premium",
                  value: buyIntent ? formatCurrency(buyIntent.annual_premium) : null,
                  hideWhenEmpty: true,
                },
                {
                  label: "Selected payment",
                  value: buyIntent
                    ? buyIntent.payment_type === "Monthly" && buyIntent.instalments
                      ? `Monthly over ${buyIntent.instalments} months`
                      : buyIntent.payment_type
                    : null,
                  hideWhenEmpty: true,
                },
                {
                  label: "Selected provider reference",
                  value: buyIntent?.provider_reference ? (
                    <CopyableId value={buyIntent.provider_reference} truncate={false} />
                  ) : null,
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy Noetica customer ID",
                  value: buyIntent?.noetica_customer_id ? (
                    <CopyableId value={buyIntent.noetica_customer_id} truncate={false} />
                  ) : null,
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy Noetica queue ID",
                  value: buyIntent?.noetica_queue_id,
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy queued at",
                  value: formatDateTime(buyIntent?.queued_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy queue attempts",
                  value: buyIntent?.attempt_count,
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy next retry",
                  value: formatDateTime(buyIntent?.next_retry_at),
                  hideWhenEmpty: true,
                },
                {
                  label: "Buy queue error",
                  value: buyIntent?.error,
                  hideWhenEmpty: true,
                },
                { label: "Assigned user", value: quote.user, hideWhenEmpty: true },
              ]}
            />
          </Card>
        )}

        {tab === "driver" &&
          (mainDriver ? (
            <DriverPanel isMain driver={mainDriver} quote={quote} title="Main driver" />
          ) : (
            <Card>
              <p className="text-sm text-content-muted">No driver recorded on this quote.</p>
            </Card>
          ))}

        {tab === "additional" &&
          (additionalDrivers.length > 0 ? (
            <div className="space-y-6">
              {additionalDrivers.map((driver, index) => (
                <DriverPanel
                  key={index}
                  driver={driver}
                  quote={quote}
                  title={`Additional driver ${index + 1}`}
                />
              ))}
            </div>
          ) : (
            <NoAdditionalDrivers />
          ))}

        {tab === "vehicle" &&
          (vehicles.length > 0 ? (
            <div className="space-y-6">
              {vehicles.map((vehicle, index) => (
                <VehiclePanel key={index} vehicle={vehicle} quote={quote} index={index} />
              ))}
            </div>
          ) : (
            <Card>
              <p className="text-sm text-content-muted">No vehicle recorded on this quote.</p>
            </Card>
          ))}

        {tab === "quotes" && (
          <ProviderQuotesPanel
            baseQuotes={baseQuotes}
            reconciliationDeadlineAt={quote.marketplace_reconciliation_deadline_at}
            reconciliationProcessedAt={quote.marketplace_reconciliation_processed_at}
          />
        )}
      </TabPanel>
    </>
  );
}
